import classNames from 'classnames';
import * as d3 from 'd3';
import PropTypes from 'prop-types';
import React, { Component } from 'react';

import defined from '~app/core/defined';

const compactXAxisLabels = ["Jan.", "", "", "", "", "",
                            "", "", "", "", "", "Dec."];
const fullXAxisLabels = ["Jan.", "Feb.", "Mar.", "Apr.", "May", "Jun.",
                         "Jul.", "Aug.", "Sep.", "Oct.", "Nov.", "Dec."];

// damato: TODO: Make the y-scales dynamic to accommodate the extreme regions.
// Format is [precip y-value, temp y-value]
// X-values are implicitly from 0 -> width.
const gridData = [
  [   0, -50],
  [ 150, -40],
  [ 300, -30],
  [ 450, -20],
  [ 600, -10],
  [ 750,   0],
  [ 900,  10],
  [1050,  20],
  [1200,  30],
  [1350,  40],
  [1500,  50],
];

const _monthLabelHeight = 17.0;
const _monthValuesWidth = 30.0;
const _monthValuesHeight = 24.0;
const _monthLabelRotation = -45.0;

const _topPadding = 8.0;

const _labelHeight = 9.5;

const _precipitationLabelWidth = 20.0;
const _precipitationLabelHeight = 120.0;
const _precipitationValuesWidth = 35.0;
const _precipitationMargin = 5.0;

const _temperatureLabelWidth = 20.0;
const _temperatureLabelHeight = 120.0;
const _temperatureValuesWidth = 35.0;
const _temperatureMargin = 5.0;

const _precipitationBarSinkValue = 3.0;

// These can be specified in css for Chrome, but not Safari or Firefox.
const _precipBarCornerRadius = 3.0;
const _tempPointCompactRadius = 3.0;
const _tempPointFullRadius = 7.5;

class ClimatogramPlot extends Component {

  constructor(props) {
    super(props);

    this.state = {
      width: 0.0,
      height: 0.0,
    };

    // Only bind once to make sure our removeEventListener is dealing with the
    // same function instance.
    this._renderClimatogram = ::this.renderClimatogram;
  }

  get plotPadding() {
    return {
      top: _topPadding,
      bottom: (_monthLabelHeight + _monthValuesHeight),
      left: (_precipitationLabelWidth + _precipitationValuesWidth + _precipitationMargin),
      right: (_temperatureLabelWidth + _temperatureValuesWidth + _temperatureMargin),
    };
  }

  get plotWidth() {
    const plotWidth =
      this.state.width -
      this.plotPadding.left -
      this.plotPadding.right;

    return Math.max(plotWidth, 0.0);
  }

  get plotHeight() {
    const plotHeight =
      this.state.height -
      this.plotPadding.top -
      this.plotPadding.bottom;

    return Math.max(plotHeight, 0.0);
  }

  componentWillMount() {
    window.addEventListener("resize", this._renderClimatogram);
  }

  componentDidMount() {
    this._renderClimatogram();
  }

  componentDidUpdate() {
    this._renderClimatogram();
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this._renderClimatogram);
  }

  extractDimensions() {
    // The original implementation of this function used the clientWidth and
    // clientHeight properties, but Firefox seems to have a bug where these are
    // always (0, 0), even for the outermost svg element.
    // https://bugzilla.mozilla.org/show_bug.cgi?id=874811

    // The outer svg gets its dimensions set normally via css.
    //
    // The inner svg gets its dimensions set according to its
    // content's bounding box. This means that those dimensions are (0, 0) until
    // we render some content into the svg, but we need those dimensions to
    // render the content.
    // We can instead use the width and height properties to get the values
    // explicitly specified as svg props.
    const {
      width: boundingWidth,
      height: boundingHeight,
    } = this.svg.getBoundingClientRect();

    if (boundingWidth !== this.state.width ||
        boundingHeight !== this.state.height) {
      this.setState({
        width: boundingWidth,
        height: boundingHeight,
      });
    }
  }

  // damato: TODO: Implement shouldComponentUpdate to avoid unnecessary re-renders.
  // We should re-render if our temp/precip data changes, or if the width/height changes.
  // I suppose also if the compact flag changes, but that probably will never happen.
  //
  // I took a stab at this and found that we don't get the componentDidUpdate
  // call where we query for a new size if we decide not to render. That makes
  // sense, but it means that we'll need another way of getting the size.
  // Perhaps we can watch it from the containing component and pass it in as
  // a prop?

  render() {
    return (
      <svg className="ClimatogramPlot"
        ref={elem => this.svg = elem}
      />
    );
  }

  renderClimatogram() {
    // Make sure we have valid dimensions before attempting to render.
    this.extractDimensions();

    // Kill all of the old groups.
    d3.select(this.svg)
      .selectAll("g")
      .remove();
    d3.select(this.svg)
      .selectAll("svg")
      .remove();

    // Generate scales.
    const precipScale = this.generateScale(gridData.map(pair => pair[0]));
    const tempScale = this.generateScale(gridData.map(pair => pair[1]));

    // Render our axis labels.
    const rootSvg = d3.select(this.svg);
    this.renderMonthLabel(rootSvg, this.props.compact);
    this.renderPrecipLabel(rootSvg, this.props.compact);
    this.renderTempLabel(rootSvg, this.props.compact);

    // Render our axis values.
    this.renderMonthValues(rootSvg, this.props.compact);
    this.renderPrecipValues(rootSvg, precipScale, this.props.compact);
    this.renderTempValues(rootSvg, tempScale, this.props.compact);

    // Render our new graph.
    const graphSvg = this.renderGraph(rootSvg);
    this.renderGridLines(graphSvg, precipScale, tempScale);
    this.renderPrecip(graphSvg, precipScale, this.props.compact);
    this.renderTemp(graphSvg, tempScale, this.props.compact);
  }

  generateScale(data) {
    const dataMin = d3.min(data);
    const dataMax = d3.max(data);

    return d3.scaleLinear()
      .domain([dataMin, dataMax])
      .range([0, this.plotHeight]);
  }

  // Graph

  renderGraph(rootSvgSelection) {
    return rootSvgSelection
      .append("svg")
      .attr("class", "ClimatogramPlot__plotArea")
      .attr("x", this.plotPadding.left)
      .attr("y", this.plotPadding.top)
      .attr("width", this.plotWidth)
      .attr("height", this.plotHeight);
  }

  // Axis Labels

  renderMonthLabel(rootSvgSelection, compact) {
    if (!compact) {
      const labelGroup = rootSvgSelection
        .append("g");

      const values = ["Months"];

      labelGroup
        .selectAll("text")
        .data(values)
        .enter()
        .append("text");

      labelGroup
        .selectAll("text")
        .attr("class", classNames("ClimatogramPlot__monthLabel", {
          "ClimatogramPlot__monthLabel--compact": compact,
          "ClimatogramPlot__monthLabel--full": !compact,
        }))
        .attr("x", this.state.width / 2.0)
        .attr("y", this.state.height)
        .text((d) => `${d}`);
    }
  }

  renderPrecipLabel(rootSvgSelection, compact) {
    const xLabel = _precipitationLabelWidth;
    const yLabel = this.plotPadding.top + this.plotHeight / 2.0;

    const labelGroup = this.renderVerticalLabel(
      rootSvgSelection,
      "Precipitation (mm.)",
      xLabel,
      yLabel,
      compact,
      "ClimatogramPlot__precipLabel"
    );

    const iconWidth = 6.0;
    const iconHeight = 15.0;

    labelGroup.append("rect");
    labelGroup.selectAll("rect")
      .attr("class", "ClimatogramPlot__precipBar")
      // The -5.0 centers the icon underneath the precip text,
      // which does not really take up its full _precipitationLabelWidth
      // allocated space.
      .attr("x", xLabel - iconWidth / 2.0 - 5.0)
      .attr("y", yLabel + _precipitationLabelHeight / 2.0)
      .attr("width", iconWidth)
      .attr("height", iconHeight)
      .attr("rx", _precipBarCornerRadius);
  }

  renderTempLabel(rootSvgSelection, compact) {
    const xLabel = this.state.width - _temperatureLabelWidth / 2.0;
    const yLabel = this.plotPadding.top + this.plotHeight / 2.0;

    const labelGroup = this.renderVerticalLabel(
      rootSvgSelection,
      "Temperature (°C)",
      xLabel,
      yLabel,
      compact,
      "ClimatogramPlot__tempLabel"
    );

    const iconRadius = 3.0;

    labelGroup.append("circle");
    labelGroup.selectAll("circle")
      .attr("class", "ClimatogramPlot__tempPoint")
      // The -2.0 centers the icon underneath the temp text,
      // which does not really take up its full _temperatureLabelWidth
      // allocated space.
      .attr("cx", xLabel - iconRadius - 2.0)
      .attr("cy", yLabel - _temperatureLabelHeight / 2.0)
      .attr("r", iconRadius);
  }

  renderVerticalLabel(rootSvgSelection, label, x, y, compact, baseClass) {
    const labelGroup = rootSvgSelection
      .append("g");

    const values = [label];

    labelGroup
      .selectAll("text")
      .data(values)
      .enter()
      .append("text");

    labelGroup
      .selectAll("text")
      .attr("class", classNames(baseClass, {
        [`${baseClass}--compact`]: compact,
        [`${baseClass}--full`]: !compact,
      }))
      .attr("x", x)
      .attr("y", y)
      .attr("transform", `rotate(-90 ${x} ${y})`)
      .text((d) => `${d}`);

      return labelGroup;
  }

  // Grid

  renderMonthValues(rootSvgSelection, compact) {
    const labelsGroup = rootSvgSelection
      .append("g");

    const values = compact ? compactXAxisLabels : fullXAxisLabels;

    labelsGroup
      .selectAll("text")
      .data(values)
      .enter()
      .append("text");

    const xFunc = (d, i) => this.plotPadding.left + (i + 0.5) * this.plotWidth / values.length;
    const yFunc = (d) => (this.plotHeight + this.plotPadding.top + _monthValuesHeight);

    labelsGroup
      .selectAll("text")
      .attr("class", classNames("ClimatogramPlot__monthValue", {
        "ClimatogramPlot__monthValue--compact": compact,
        "ClimatogramPlot__monthValue--full": !compact,
      }))
      .attr("x", (d, i) => xFunc(d, i))
      .attr("y", (d) => yFunc(d))
      .attr("width", _monthValuesWidth)
      .attr("height", _monthValuesHeight)
      .attr("transform", (d, i) => `rotate(${_monthLabelRotation} ${xFunc(d, i)} ${yFunc(d)})`)
      .text((d) => `${d}`);
  }

  renderPrecipValues(rootSvgSelection, precipScale, compact) {
    this.renderVerticalAxisLabels(
      rootSvgSelection,
      gridData.map(pair => pair[0]),
      this.plotPadding.left - _precipitationMargin,
      _precipitationValuesWidth,
      precipScale,
      compact,
      "ClimatogramPlot__precipValue"
    );
  }

  renderTempValues(rootSvgSelection, tempScale, compact) {
    this.renderVerticalAxisLabels(
      rootSvgSelection,
      gridData.map(pair => pair[1]),
      // The * 0.5 here moves the temp labels in their allocated area towards
      // the plot area.
      this.plotPadding.left + this.plotWidth + _temperatureMargin + _temperatureValuesWidth * 0.5,
      _temperatureValuesWidth,
      tempScale,
      compact,
      "ClimatogramPlot__tempValue"
    );
  }

  renderVerticalAxisLabels(
    rootSvgSelection,
    values,
    xBase,
    labelWidth,
    scale,
    compact,
    baseClass) {

    const labelsGroup = rootSvgSelection
      .append("g");

    labelsGroup
      .selectAll("text")
      .data(values)
      .enter()
      .append("text");

    labelsGroup
      .selectAll("text")
      .attr("class", classNames(baseClass, {
        [`${baseClass}--compact`]: compact,
        [`${baseClass}--full`]: !compact,
      }))
      .attr("x", xBase)
      .attr("y", (d) => (this.plotHeight + this.plotPadding.top) - (scale(d) - _labelHeight / 3.0))
      .attr("width", labelWidth)
      .attr("height", _labelHeight)
      .text((d) => `${d}`);
  }

  renderGridLines(rootSvgSelection, precipScale, tempScale) {
    const gridLineGroup = rootSvgSelection
      .append("g");

    // Convert from [[precip y-value, temp y-value]] to
    // [line], where line is defined as [[x0, y], [x1, y]].
    const gridLineData = gridData.reduce((lines, yValues, i) => {
      const line = [[0, precipScale(yValues[0])], [this.plotWidth, tempScale(yValues[1])]];
      return [...lines, line];
    }, []);
    const lineFunction = d3.line()
      .x((d) => d[0])
      .y((d) => this.plotHeight - d[1]);

    gridLineGroup
      .selectAll("path")
      .data(gridLineData)
      .enter()
      .append("path");

    gridLineGroup
      .selectAll("path")
      .attr("class", "ClimatogramPlot__gridLine")
      .attr("d", (d) => lineFunction(d));

  }

  // Precipitation

  renderPrecip(rootSvgSelection, precipScale, compact) {
    // We still try to render the climatogram, even if there is no data
    // so that we can show the frame with an empty plot.
    if (!defined(this.props.precipData) || this.props.precipData.length <= 0) {
      return;
    }

    const precipDataGroup = rootSvgSelection
      .append("g");

    precipDataGroup
      .selectAll("rect")
      .data(this.props.precipData)
      .enter()
      .append("rect");

    const width = this.plotWidth * this.barWidthMultiplier(compact);

    precipDataGroup
      .selectAll("rect")
      .data(this.props.precipData)
      .attr("class", "ClimatogramPlot__precipBar")
      .attr("x", (d, i) => (i + 0.5) * this.plotWidth / this.props.precipData.length - width / 2.0)
      .attr("y", (d) => this.plotHeight - precipScale(d))
      .attr("width", width)
      .attr("height", (d) => precipScale(d) + _precipitationBarSinkValue)
      .attr("rx", _precipBarCornerRadius);
  }

  barWidthMultiplier(compact) {
    let width = 0.0;

    if (compact) {
      // The compact mockup has bars with width of 6 and spaces of width 5.
      // So (12 * 6) + (13 * 5) is 137, and 6 / 137 is 0.044.
      width = 0.044;
    }
    else {
      // The 0.053 width multiplier comes from the Sketch mockup. The bars there have width 15
      // and the spaces between bars have width 8. So (12 * 15) + (13 * 8) is 284, and 15 / 284 is 0.053.
      width = 0.053;
    }
    return width;
  }

  // Temperature

  renderTemp(rootSvgSelection, tempScale, compact) {
    // We still try to render the climatogram, even if there is no data
    // so that we can show the frame with an empty plot.
    if (!defined(this.props.tempData) || this.props.tempData.length <= 0) {
      return;
    }

    // Temp data is an array of data that we need for a single path.
    // We only should have one path to represent this entire data set,
    // so enclose it in a 1-element array.
    const pathData = [this.props.tempData];

    const centerXFunc = (d, i) => (i + 0.5) * this.plotWidth / this.props.tempData.length;
    const yFunc = (d) => this.plotHeight - tempScale(d);

    const lineFunction = d3.line()
      .x(centerXFunc)
      .y(yFunc);

    const tempDataGroup = rootSvgSelection
      .append("g");

    // Lines.
    tempDataGroup
      .selectAll("path")
      .data(pathData)
      .enter()
      .append("path");

    tempDataGroup
      .selectAll("path")
      .attr("class", classNames("ClimatogramPlot__tempLine", {
        "ClimatogramPlot__tempLine--compact": compact,
        "ClimatogramPlot__tempLine--full": !compact,
      }))
      .attr("d", (d) => lineFunction(d));

    // Circles.
    tempDataGroup
      .selectAll("circle")
      .data(this.props.tempData)
      .enter()
      .append("circle");

    tempDataGroup
      .selectAll("circle")
      .data(this.props.tempData)
      .attr("class", "ClimatogramPlot__tempPoint")
      .attr("cx", centerXFunc)
      .attr("cy", yFunc)
      .attr("r", compact ? _tempPointCompactRadius : _tempPointFullRadius);
  }
}

ClimatogramPlot.PropTypes = {
  tempData: PropTypes.array,
  precipData: PropTypes.array,
  compact: PropTypes.bool,
};

export default ClimatogramPlot;



// WEBPACK FOOTER //
// ./src/components/ClimatogramPlot.js